import { CLASSES } from '../core/characterUtils';
import * as st from '../core/st';

const ELEMENT_BY_STAT_ID = {
    [st.INTELLIGENCE_ID]: { words: ['feu', 'fire', 'intelligence'], fr: 'Feu', en: 'Fire', de: 'Feuer', es: 'Fuego', pt: 'Fogo' },
    [st.CHANCE_ID]: { words: ['eau', 'water', 'chance'], fr: 'Eau', en: 'Water', de: 'Wasser', es: 'Agua', pt: 'Água' },
    [st.AGILITY_ID]: { words: ['air', 'agility', 'agilite', 'agilité'], fr: 'Air', en: 'Air', de: 'Luft', es: 'Aire', pt: 'Ar' },
    [st.STRENGTH_ID]: { words: ['terre', 'earth', 'force', 'strength'], fr: 'Terre', en: 'Earth', de: 'Erde', es: 'Tierra', pt: 'Terra' },
};

const TOKEN_RE = /[0-9]+\/[0-9]+|[\w]+/gi;
const AI_NAME_RE = /\b(ai|assistant|chatgpt|gemini)\b/i;
const JUNK_RE = /^(x+d+|lol+|mdr+|ptdr+|wtf+|ok+|oui+|non+|sa|mere|merde|pute|fdp|tg|nique|con+|salope|fuck|shit|damn|bruh|kek+|rip+|gg+|ez+)$/i;

const STOP_WORDS = new Set([
    'full', 'max', 'lvl', 'level', 'niveau', 'stuff', 'build', 'set', 'panoplie',
    'gros', 'maxi', 'degats', 'dommages', 'damage', 'dps', 'optimise', 'optimize',
    'optimiser', 'generator', 'genere', 'generate', 'pour', 'for', 'avec', 'with',
    'mon', 'my', 'un', 'une', 'le', 'la', 'les', 'the', 'a', 'de', 'du', 'des',
    'en', 'et', 'and', 'ou', 'or', 'sur', 'on', 'je', 'veux', 'want', 'besoin',
    'need', 'please', 'svp', 'stp', 'merci', 'thanks', 'juste', 'only', 'seulement',
    'tres', 'very', 'super', 'ultra', 'mega', 'bon', 'good', 'best', 'meilleur',
    'optimal', 'parfait', 'perfect',
]);

function classLabel(slug, lang) {
    const row = CLASSES.find((c) => c.name === slug);
    if (row?.label) return row.label[lang] || row.label.fr || row.label.en;
    return slug ? String(slug).charAt(0).toUpperCase() + String(slug).slice(1) : 'Build';
}

function classAliases(slug) {
    const row = CLASSES.find((c) => c.name === slug);
    const aliases = new Set([slug]);
    if (row?.label) {
        aliases.add(String(row.label.fr || '').toLowerCase());
        aliases.add(String(row.label.en || '').toLowerCase());
    }
    return aliases;
}

function tokenize(text) {
    const out = [];
    const re = new RegExp(TOKEN_RE.source, TOKEN_RE.flags);
    let m;
    while ((m = re.exec(String(text || ''))) !== null) {
        out.push(m[0].toLowerCase());
    }
    return out;
}

function criteriaElementStatIds(req) {
    const stats = req?.stats_criteria || {};
    const out = new Set();
    for (const statId of Object.keys(ELEMENT_BY_STAT_ID)) {
        const rule = stats[statId] || stats[String(statId)];
        if (rule?.maximize) out.add(Number(statId));
    }
    return out;
}

function tokenElementStatId(token) {
    const low = token.toLowerCase();
    for (const [statId, meta] of Object.entries(ELEMENT_BY_STAT_ID)) {
        if (meta.words.includes(low)) return Number(statId);
    }
    return null;
}

function elementDisplay(statId, lang) {
    const meta = ELEMENT_BY_STAT_ID[statId];
    if (!meta) return '';
    return (meta[lang] || meta.fr || '').toLowerCase();
}

function sanitizeSuggestedName(raw) {
    const name = String(raw || '').trim();
    if (!name || AI_NAME_RE.test(name)) return null;
    return name.slice(0, 80);
}

function elementSuffix(req, lang) {
    const elems = [...criteriaElementStatIds(req)];
    if (elems.length !== 1) return null;
    return ELEMENT_BY_STAT_ID[elems[0]]?.[lang] || ELEMENT_BY_STAT_ID[elems[0]]?.fr;
}

function apPmSuffix(req) {
    const stats = req?.stats_criteria || {};
    const pa = Number(stats['1']?.min || stats[1]?.min || 0);
    const pm = Number(stats['23']?.min || stats[23]?.min || 0);
    if (pa > 0 && pm > 0) return `${pa}/${pm}`;
    return null;
}

function primaryStatSuffix(req, lang) {
    const stats = req?.stats_criteria || {};
    for (const [key, rule] of Object.entries(stats)) {
        if (!rule?.maximize) continue;
        const statId = Number(key);
        if (ELEMENT_BY_STAT_ID[statId]) continue;
        const def = st.STATS.find((s) => Number(s.id) === statId);
        const label = def?.label?.[lang] || def?.label?.fr || def?.label?.en;
        if (label) return label;
    }
    return null;
}

function flavorAligned(userRequest, req, classSlug) {
    const tokens = tokenize(userRequest);
    if (!tokens.length) return false;
    const aliases = classAliases(classSlug);
    if (!tokens.some((t) => aliases.has(t))) return false;
    const critElems = criteriaElementStatIds(req);
    if (!critElems.size) return true;
    const msgElems = new Set(tokens.map(tokenElementStatId).filter(Boolean));
    for (const id of critElems) {
        if (msgElems.has(id)) return true;
    }
    return false;
}

function flavorNameFromRequest(userRequest, { req, draft, lang }) {
    const text = String(userRequest || '').trim();
    if (!text) return null;

    const classSlug = draft?.draft_metadata?.characterClass || 'iop';
    if (!flavorAligned(text, req, classSlug)) return null;

    const critElems = criteriaElementStatIds(req);
    const apPm = apPmSuffix(req);
    const aliases = classAliases(classSlug);
    const elementWords = new Set();
    critElems.forEach((statId) => {
        (ELEMENT_BY_STAT_ID[statId]?.words || []).forEach((w) => elementWords.add(w));
    });

    const out = [];
    let seenClass = false;
    for (const token of tokenize(text)) {
        if (JUNK_RE.test(token) || STOP_WORDS.has(token)) continue;
        if (/^\d+$/.test(token) && ['200', '1', '199'].includes(token)) continue;

        if (aliases.has(token)) {
            if (!seenClass) {
                out.push(classSlug);
                seenClass = true;
            }
            continue;
        }

        const elemId = tokenElementStatId(token);
        if (elemId != null) {
            if (critElems.has(elemId)) {
                const word = elementDisplay(elemId, lang);
                if (word && out[out.length - 1] !== word) out.push(word);
            }
            continue;
        }

        if (/^\d+\/\d+$/.test(token) && apPm && token === apPm) {
            out.push(token);
            continue;
        }

        if (token.length < 2 || elementWords.has(token)) continue;
        out.push(token);
    }

    if (!seenClass || out.length < 2) return null;
    return sanitizeSuggestedName(out.join(' '));
}

function defaultBuildName({ req, classSlug, lang }) {
    const parts = [classLabel(classSlug, lang)];
    const element = elementSuffix(req, lang);
    if (element) parts.push(element);
    else {
        const role = primaryStatSuffix(req, lang);
        if (role) parts.push(role);
    }
    const apPm = apPmSuffix(req);
    if (apPm) parts.push(apPm);
    return parts.join(' ');
}

/** Descriptive stuff name for AI-generated builds (never includes "AI"). */
export function suggestAiBuildName({ draft, lang = 'fr', userRequest = '' }) {
    const metadata = draft?.draft_metadata || {};
    const req = draft?.req || {};
    const classSlug = metadata.characterClass || 'iop';

    const flavor = flavorNameFromRequest(userRequest, { req, draft, lang });
    if (flavor) return flavor;

    const fromApi = sanitizeSuggestedName(metadata.suggestedName);
    if (fromApi) return fromApi;

    return defaultBuildName({ req, classSlug, lang });
}
